import { Tooltip } from '@integrations/anchored-tooltip'
import cn from '@integrations/classnames'
import parse from '@integrations/html'
import { Fragment, type ReactNode } from '@integrations/react'
import { createPortal } from '@integrations/renderer'
import styles from './LegacyTooltip.module.scss'

interface LegacyTooltipProps {
  id: string
  type?: 'dark' | 'light' | 'success' | 'warning' | 'error' | 'info'
  place?: 'top' | 'right' | 'bottom' | 'left'
  className?: string
  delayShow?: number
  clickable?: boolean
  html?: boolean
  padding?: string
  getContent?: (content?: string | null) => ReactNode
  effect?: string
  multiline?: boolean
  isCapture?: boolean
}

// react-tooltip v4 turned "<br>" and line breaks into real line breaks when `multiline` was set; callers still rely on it.
function renderMultiline(text: string) {
  return text.split(/<br\s*\/?>|\r?\n/i).map((line, index) => (
    <Fragment key={`${index}-${line}`}>
      {index > 0 && <br />}
      {line}
    </Fragment>
  ))
}

function LegacyTooltip({
  id,
  type = 'dark',
  place,
  className,
  delayShow,
  clickable,
  html,
  padding,
  multiline,
  getContent
}: LegacyTooltipProps) {
  const tooltip = (
    <Tooltip
      useExternalStyles
      id={id}
      anchorSelect={`[data-for='${id}']`}
      type={type}
      place={place}
      positionStrategy='fixed'
      className={cn(styles['legacy-tooltip'], 'legacy-tooltip', className)}
      delayShow={delayShow}
      clickable={clickable}
      style={padding ? { padding } : undefined}
      render={({ activeAnchor }) => {
        const content = activeAnchor?.getAttribute('data-tip')
        const rendered = getContent ? getContent(content) : content
        if (typeof rendered === 'string') {
          if (html) return parse(rendered)
          if (multiline) return renderMultiline(rendered)
        }
        return rendered
      }}
    />
  )

  return typeof document === 'undefined' ? tooltip : createPortal(tooltip, document.body)
}

LegacyTooltip.rebuild = () => undefined

export default LegacyTooltip
